import React from "react";
import { useGetGroupedData } from "../../../__shared/useGetGroupedData";
import { useSnapshot } from "@iliotech/data-wire";
import { AllocationOption } from "@iliotech/data-wire/src/reactHooks/context/StructureGrouping/types";
import { PortfolioSnapshotDtoV3 } from "@iliotech/generated-api-v3";

type UseGetChartDataProps = {
  portfolioId: string;
  grouping: AllocationOption;
  selectedGroup?: { code: string; label: string };
};

export const useGetChartData = ({
  portfolioId,
  grouping,
  selectedGroup,
}: UseGetChartDataProps) => {
  const { getGroupedData, getGroupedTableData } = useGetGroupedData({
    grouping,
    selectedGroup,
    t: () => "",
  });
  const snapshots = useSnapshot(portfolioId)?.data?.data;
  const { closedPositions, openPositions } = React.useMemo(() => {
    const closedPositions: PortfolioSnapshotDtoV3[] = [];
    const openPositions: PortfolioSnapshotDtoV3[] = [];
    snapshots?.forEach((snapshot) => {
      if (snapshot.marketValueBase === 0) {
        closedPositions.push(snapshot);
      } else {
        openPositions.push(snapshot);
      }
    });
    return { closedPositions, openPositions };
  }, [snapshots]);

  const getChartData = React.useCallback(
    (hideClosedPositions: boolean) => {
      const groupedData = getGroupedData(
        hideClosedPositions ? openPositions : snapshots
      );

      const columnChartData = Object.values(groupedData).reduce(
        (prev, curr) => [...prev, ...curr],
        []
      );

      const sortedColumnChartData = [...columnChartData].sort(
        (a, b) => Math.abs(b.value) - Math.abs(a.value)
      );

      const tableData = getGroupedTableData(
        hideClosedPositions ? openPositions : snapshots
      );

      return {
        columnChartData: sortedColumnChartData,
        tableData: tableData,
      };
    },
    [openPositions, snapshots, getGroupedData, getGroupedTableData]
  );

  return { getChartData };
};
